import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { Observable, of } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';

import { OperatorConfigService } from '../operator-config/operator-config.service';

@Injectable({
  providedIn: 'root'
})
export class OperatorTranslationService {

  // Sparse per-operator translation overrides, layered on top of the base
  // public/i18n/{lang}.json files.
  //
  // The data comes from OperatorConfigService - the `translations` block of the
  // merged per-operator registry - not from a fetch of our own. That stream is
  // already timed out and caught to {}, so applying an override can never error
  // or hang here.
  private activeOperator: string | null = null;

  constructor(
    private readonly translate: TranslateService,
    private readonly operatorConfigService: OperatorConfigService
  ) {
    // ngx-translate's HttpLoader re-fetches the base file on every language
    // switch, which wipes any prior setTranslation() merge - reapply the
    // active operator's override whenever the language changes. Same
    // "reapply on onLangChange" pattern used elsewhere in the app for
    // values baked in once and never refreshed (see my-bets.component.ts).
    this.translate.onLangChange.subscribe(event => {
      if (this.activeOperator) {
        this.applyOverridesForLang(this.activeOperator, event.lang).subscribe();
      }
    });
  }

  /**
   * Apply an operator's translation overrides for the currently active
   * language, and keep reapplying them across later language changes until
   * clearOperatorTranslations() is called.
   */
  applyOperatorTranslations(operatorName: string): Observable<boolean> {

    const normalizedOperator = this.normalizeOperatorName(operatorName);

    this.activeOperator = normalizedOperator;

    const lang = this.translate.currentLang || this.translate.getDefaultLang();

    return this.applyOverridesForLang(normalizedOperator, lang);
  }

  /**
   * Stop reapplying overrides on future language changes.
   *
   * Does not retroactively strip already-merged keys from the current
   * language's translation store (ngx-translate has no targeted "unmerge");
   * a subsequent reloadLang()/page reload resets to the base file.
   */
  clearOperatorTranslations(): void {
    this.activeOperator = null;
  }

  private applyOverridesForLang(operatorName: string, lang: string): Observable<boolean> {

    if (!lang) {
      return of(false);
    }

    // Already sanitized by OperatorConfigService, so what arrives here is a
    // plain string map or nothing at all.
    return this.operatorConfigService.getOperatorTranslations(operatorName).pipe(
      take(1),
      map(translations => translations[lang]),
      tap(overrides => {
        if (overrides && Object.keys(overrides).length) {
          this.translate.setTranslation(lang, overrides, true);
        }
      }),
      map(overrides => !!overrides && !!Object.keys(overrides).length)
    );
  }

  private normalizeOperatorName(operatorName: string): string {

    return (operatorName || '')
      .trim()
      .toLowerCase();
  }
}
